import { Component } from 'react';
import { ConverterLayout, layoutStyles } from '../converter-layout/converter-layout';
import { CodeBlock } from '../converter-layout/code-block';
import { PersistedInput } from '../../../utils/persisted-input';

type Format = 'toml' | 'ini' | 'properties' | 'env' | 'hcl' | 'graphql';

const SLUG_TO_FORMAT: Record<string, Format> = {
    'toml-formatter': 'toml',
    'ini-formatter': 'ini',
    'properties-formatter': 'properties',
    'env-formatter': 'env',
    'hcl-formatter': 'hcl',
    'graphql-formatter': 'graphql',
};

const FORMAT_LABEL: Record<Format, string> = {
    toml: 'TOML',
    ini: 'INI',
    properties: 'Java Properties',
    env: '.env',
    hcl: 'HCL / Terraform',
    graphql: 'GraphQL',
};

const SAMPLES: Record<Format, string> = {
    toml: `title="Cargo Manifest"
[package]
name="my-crate"
version="0.1.0"
authors=["you@example.com"]

[dependencies]
serde={ version="1", features=["derive"] }
tokio={ version="1.32" }`,
    ini: `[Server]
host=127.0.0.1
port=8080
debug=true

[Database]
url=postgres://localhost/mydb
pool_size=10`,
    properties: `app.name=MyApp
app.version=1.2.3
server.port=8080
spring.datasource.url=jdbc:postgresql://localhost/mydb
spring.datasource.username=admin`,
    env: `DATABASE_URL=postgres://localhost/mydb
REDIS_URL=redis://localhost:6379
NODE_ENV=development
PORT=3000
SECRET_KEY="some long secret string"`,
    hcl: `terraform {
  required_version = ">= 1.0"
  required_providers {
    aws = { source = "hashicorp/aws", version = "~> 5.0" }
  }
}
resource "aws_instance" "web" {
  ami = "ami-0c55b159cbfafe1f0"
  instance_type = "t3.micro"
  tags = { Name = "web-server" }
}`,
    graphql: `type Query {
type Article {
  id: ID!
  title: String!
  body: String!
  author: User!
  publishedAt: DateTime
  tags: [String!]!
}
}
extend type Query {
  article(id:ID!):Article
  articles(limit:Int=10):[Article!]!
}`,
};

interface State {
    format: Format;
    input: string;
    output: string;
    error: string | null;
    copied: boolean;
}

type LibState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; mod: any }
    | { status: 'error'; message: string };

export default class DevFormatFormatterComponent extends Component<{ feature?: string }, State & { libToml: LibState; libIni: LibState; libGraphql: LibState }> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private saveTimer: ReturnType<typeof setTimeout> | null = null;

    private get storageKey(): string {
        return `dev-format-formatter:${this.props.feature || 'default'}`;
    }

    constructor(props: { feature?: string }) {
        super(props);
        const format = SLUG_TO_FORMAT[props.feature || ''] || 'toml';
        const restored = PersistedInput.load(
            `dev-format-formatter:${props.feature || 'default'}`,
            { input: SAMPLES[format] },
            (v): v is { input: string } =>
                !!v && typeof v === 'object' && typeof (v as any).input === 'string',
        );
        this.state = {
            format,
            input: restored.input,
            output: '',
            error: null,
            copied: false,
            libToml: { status: 'idle' },
            libIni: { status: 'idle' },
            libGraphql: { status: 'idle' },
        };
    }

    componentDidMount() {
        this.format();
    }

    componentDidUpdate(_: any, prev: State) {
        if (prev.input !== this.state.input) {
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => {
                PersistedInput.save(this.storageKey, { input: this.state.input });
            }, 500);
        }
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
        if (this.saveTimer) clearTimeout(this.saveTimer);
    }

    private async loadLibFor(format: Format): Promise<any | null> {
        switch (format) {
            case 'toml': {
                if (this.state.libToml.status === 'ready') return this.state.libToml.mod;
                this.setState({ libToml: { status: 'loading' } });
                try {
                    const mod = await import('@iarna/toml');
                    this.setState({ libToml: { status: 'ready', mod } });
                    return mod;
                } catch (e) {
                    this.setState({ libToml: { status: 'error', message: 'Failed to load TOML library.' } });
                    return null;
                }
            }
            case 'ini': {
                if (this.state.libIni.status === 'ready') return this.state.libIni.mod;
                this.setState({ libIni: { status: 'loading' } });
                try {
                    const mod = await import('js-ini');
                    this.setState({ libIni: { status: 'ready', mod } });
                    return mod;
                } catch (e) {
                    this.setState({ libIni: { status: 'error', message: 'Failed to load INI library.' } });
                    return null;
                }
            }
            case 'graphql': {
                if (this.state.libGraphql.status === 'ready') return this.state.libGraphql.mod;
                this.setState({ libGraphql: { status: 'loading' } });
                try {
                    const mod = await import('graphql');
                    this.setState({ libGraphql: { status: 'ready', mod } });
                    return mod;
                } catch (e) {
                    this.setState({ libGraphql: { status: 'error', message: 'Failed to load GraphQL library.' } });
                    return null;
                }
            }
            case 'properties':
            case 'env':
            case 'hcl':
                return {};
        }
    }

    format = async () => {
        const { format, input } = this.state;
        const lib = await this.loadLibFor(format);
        if (lib === null) return;
        try {
            let output = '';
            switch (format) {
                case 'toml': {
                    const obj = lib.parse(input);
                    output = lib.stringify(obj);
                    break;
                }
                case 'ini': {
                    const obj = lib.parse(input, { comment: ['#', ';'] });
                    output = lib.stringify(obj).replace(/^\n+/, '');
                    break;
                }
                case 'properties':
                case 'env': {
                    output = this.formatKeyValue(input, format);
                    break;
                }
                case 'hcl': {
                    output = this.formatHcl(input);
                    break;
                }
                case 'graphql': {
                    const ast = lib.parse(input, { noLocation: true });
                    output = lib.print(ast);
                    break;
                }
            }
            this.setState({ output, error: null, copied: false });
        } catch (e: any) {
            this.setState({ output: '', error: e?.message || 'Failed to parse / format the input.' });
        }
    };

    private formatKeyValue(src: string, format: 'env' | 'properties'): string {
        const lines = src.split(/\r?\n/);
        const out: string[] = [];
        const isComment = (s: string) =>
            s.startsWith('#') || (format === 'properties' && s.startsWith('!'));

        for (const raw of lines) {
            const trimmed = raw.replace(/\s+$/, '');
            const leading = trimmed.trimStart();
            if (leading === '') { out.push(''); continue; }
            if (isComment(leading)) { out.push(leading); continue; }

            const seps = format === 'properties' ? ['=', ':'] : ['='];
            let idx = -1;
            for (const c of seps) {
                const i = leading.indexOf(c);
                if (i > 0 && (idx === -1 || i < idx)) idx = i;
            }
            if (idx < 0) { out.push(leading); continue; }
            const key = leading.slice(0, idx).trim();
            const val = leading.slice(idx + 1).trim();
            out.push(`${key}=${val}`);
        }
        while (out.length > 0 && out[out.length - 1] === '') out.pop();
        return out.join('\n');
    }

    private formatHcl(src: string): string {
        // Simple HCL pretty-printer: 2-space indent based on { } depth.
        // Not a full parser — splits by lines, balances braces.
        const lines = src.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        const out: string[] = [];
        let depth = 0;
        for (const raw of lines) {
            // Outdent if line starts with }
            const startsWithClose = raw.startsWith('}');
            if (startsWithClose) depth = Math.max(0, depth - 1);
            // Align = within blocks for readability
            const indented = '  '.repeat(depth) + raw;
            out.push(indented);
            // Indent if line ends with { (and isn't a one-line block like { foo = 1 })
            const opens = (raw.match(/\{/g) || []).length;
            const closes = (raw.match(/\}/g) || []).length;
            depth += (opens - closes);
            if (depth < 0) depth = 0;
        }
        return out.join('\n');
    }

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { format, input, output, error, copied } = this.state;
        return (
            <ConverterLayout
                inputLabel={`${FORMAT_LABEL[format]} input`}
                inputMeta={input ? `${input.length.toLocaleString()} chars · ${input.split('\n').length.toLocaleString()} lines` : undefined}
                inputArea={
                    <textarea className={layoutStyles.area} value={input}
                        spellCheck={false}
                        placeholder={`Paste ${FORMAT_LABEL[format]} content here…`}
                        onChange={e => this.setState({ input: e.target.value, error: null, copied: false })}
                        aria-label={`${FORMAT_LABEL[format]} input`} />
                }

                outputLabel="Formatted output"
                outputMeta={output ? `${output.length.toLocaleString()} chars · ${output.split('\n').length.toLocaleString()} lines` : undefined}
                outputArea={output ? (
                    <CodeBlock code={output} language={format} wrapLines maxHeight={500} minHeight={320}
                        ariaLabel="Formatted output" />
                ) : (
                    <textarea className={layoutStyles.area} readOnly value=""
                        placeholder="Click Format to clean up your input."
                        aria-label="Formatted output" />
                )}
                outputActions={
                    <button className={`${layoutStyles.iconBtn} ${copied ? layoutStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy'}
                    </button>
                }

                error={error}

                primaryAction={
                    <button className={layoutStyles.primaryBtn} onClick={this.format}>
                        Format →
                    </button>
                }
            />
        );
    }
}
